import { useCurrentMinute } from "../../../../../hooks/use-current-minute";
import { toProxiedMediaUrl } from "../../../../../lib/media-proxy";
import type { Broadcast } from "../../../../../types/broadcasts.types";
import { useIsViewerJourney } from "../../../../viewer-feed/viewer-journey.context";
import * as CSS from "./feed-header.styles";

type FeedHeaderProps = Pick<
	Broadcast,
	| "homeTeam"
	| "awayTeam"
	| "homeLogo"
	| "awayLogo"
	| "venue"
	| "location"
	| "fixtureDate"
	| "startTime"
	| "halfTimeAt"
	| "secondHalfStartTime"
	| "fullTimeAt"
	| "timeCorrection"
	| "homeScore"
	| "awayScore"
> & {
	// Only takes effect in the viewer journey — the broadcaster's header is
	// never minimised, regardless of this prop.
	minimised?: boolean;
};

const FeedHeader = ({
	homeTeam,
	awayTeam,
	homeLogo,
	awayLogo,
	venue,
	location,
	fixtureDate,
	startTime,
	halfTimeAt,
	secondHalfStartTime,
	fullTimeAt,
	timeCorrection,
	homeScore,
	awayScore,
	minimised,
}: FeedHeaderProps) => {
	const isViewer = useIsViewerJourney();
	const isMinimised = isViewer && !!minimised;

	const currentMinute = useCurrentMinute(
		startTime,
		halfTimeAt,
		secondHalfStartTime,
		timeCorrection ?? null,
	);

	const matchTimeValue = !startTime
		? ""
		: fullTimeAt
			? "FT"
			: halfTimeAt && !secondHalfStartTime
				? "HT"
				: currentMinute !== null
					? `${currentMinute}'`
					: "";

	return (
		<CSS.FeedHeader $minimised={isMinimised}>
			{!isMinimised && (
				<>
					<CSS.DateText>{new Date(fixtureDate).toDateString()}</CSS.DateText>
					<CSS.Venue>
						{venue}, {location}
					</CSS.Venue>
				</>
			)}
			<CSS.TeamsScore $minimised={isMinimised}>
				<CSS.Team>
					<img
						width={58}
						src={toProxiedMediaUrl(homeLogo)}
						alt={`${homeTeam} badge`}
					/>
					{!isMinimised && <CSS.TeamName>{homeTeam}</CSS.TeamName>}
				</CSS.Team>
				<CSS.ScoreContainer>
					<CSS.Score>
						{homeScore} - {awayScore}
					</CSS.Score>
					<CSS.Minutes>{matchTimeValue}</CSS.Minutes>
				</CSS.ScoreContainer>
				<CSS.Team>
					<img
						width={58}
						src={toProxiedMediaUrl(awayLogo)}
						alt={`${awayTeam} badge`}
					/>
					{!isMinimised && <CSS.TeamName>{awayTeam}</CSS.TeamName>}
				</CSS.Team>
			</CSS.TeamsScore>
		</CSS.FeedHeader>
	);
};

export default FeedHeader;
